import { useState } from 'react'
import { format, parseISO, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { FileText, Download, FileSpreadsheet, Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { exportApi, netWorthApi } from '@/lib/api'
import { downloadBlob, formatCurrency } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import type { NetWorthHistory } from '@/types'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'

type PeriodOption = 'last30' | 'last60' | 'last90' | 'thisMonth' | 'lastMonth' | 'custom'

function getDateRange(period: PeriodOption): { start: string; end: string; label: string } {
  const today = new Date()

  switch (period) {
    case 'last30':
      return {
        start: format(subDays(today, 30), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
        label: 'Last 30 Days'
      }
    case 'last60':
      return {
        start: format(subDays(today, 60), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
        label: 'Last 60 Days'
      }
    case 'last90':
      return {
        start: format(subDays(today, 90), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
        label: 'Last 90 Days'
      }
    case 'thisMonth':
      return {
        start: format(startOfMonth(today), 'yyyy-MM-dd'),
        end: format(endOfMonth(today), 'yyyy-MM-dd'),
        label: format(today, 'MMMM yyyy')
      }
    case 'lastMonth':
      const lastMonth = subMonths(today, 1)
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        label: format(lastMonth, 'MMMM yyyy')
      }
    default:
      return {
        start: format(subDays(today, 30), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
        label: 'Last 30 Days'
      }
  }
}

export default function Reports() {
  const [period, setPeriod] = useState<PeriodOption>('last30')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [loadingCsv, setLoadingCsv] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [taxYear, setTaxYear] = useState(new Date().getFullYear())
  const [taxExportLoading, setTaxExportLoading] = useState<string | null>(null)
  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)
  const [netWorthMonths, setNetWorthMonths] = useState<number>(12)

  const { data: netWorthHistory } = useQuery<NetWorthHistory>({
    queryKey: ['net-worth-history', netWorthMonths],
    queryFn: async () => (await netWorthApi.getHistory(netWorthMonths)).data,
  })

  const dateRange = period === 'custom'
    ? { start: customStart, end: customEnd, label: 'Custom Range' }
    : getDateRange(period)

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const handleExportPdf = async () => {
    if (period === 'custom' && (!customStart || !customEnd)) {
      showMessage('error', 'Please select both start and end dates for custom range.')
      return
    }

    setLoadingPdf(true)
    try {
      const response = await exportApi.reportPdf({
        startDate: dateRange.start,
        endDate: dateRange.end
      })

      // Check if response is actually a PDF (not an error JSON)
      const contentType = response.headers['content-type']
      if (contentType && contentType.includes('application/json')) {
        // Server returned an error as JSON
        const text = await response.data.text()
        const error = JSON.parse(text)
        throw new Error(error.error || 'Server error')
      }

      const filename = `financial-report-${dateRange.start}-to-${dateRange.end}.pdf`
      downloadBlob(response.data, filename)
      showMessage('success', `${filename} has been downloaded.`)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      showMessage('error', 'Failed to generate PDF report. Please try again.')
    } finally {
      setLoadingPdf(false)
    }
  }

  const handleExportCsv = async (type: 'transactions' | 'bills' | 'categories') => {
    setLoadingCsv(type)
    try {
      let response
      let filename

      switch (type) {
        case 'transactions':
          response = await exportApi.transactionsCsv({
            startDate: dateRange.start,
            endDate: dateRange.end
          })
          filename = `transactions-${dateRange.start}-to-${dateRange.end}.csv`
          break
        case 'bills':
          response = await exportApi.billsCsv()
          filename = `bills-${format(new Date(), 'yyyy-MM-dd')}.csv`
          break
        case 'categories':
          response = await exportApi.categoriesCsv()
          filename = `categories-${format(new Date(), 'yyyy-MM-dd')}.csv`
          break
      }

      downloadBlob(response.data, filename)
      showMessage('success', `${filename} has been downloaded.`)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      showMessage('error', `Failed to export ${type}. Please try again.`)
    } finally {
      setLoadingCsv(null)
    }
  }

  const handleTaxExport = async (format: 'csv' | 'pdf') => {
    setTaxExportLoading(format)
    try {
      const response = format === 'csv'
        ? await exportApi.getTaxCsv(taxYear)
        : await exportApi.getTaxPdf(taxYear)
      const ext = format === 'csv' ? 'csv' : 'pdf'
      const filename = `tax-summary-${taxYear}.${ext}`
      downloadBlob(response.data, filename)
      showMessage('success', `${filename} has been downloaded.`)
    } catch (error) {
      console.error('Tax export error:', error)
      showMessage('error', `Failed to export tax summary. Please try again.`)
    } finally {
      setTaxExportLoading(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Reports & Export</h1>
        <p className="text-muted-foreground">Generate reports and export your financial data</p>
      </div>

      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Date Range</CardTitle>
          <CardDescription>Select the time period for your report</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-2">
              <Label>Period</Label>
              <Select value={period} onValueChange={(v) => setPeriod(v as PeriodOption)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last30">Last 30 Days</SelectItem>
                  <SelectItem value="last60">Last 60 Days</SelectItem>
                  <SelectItem value="last90">Last 90 Days</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {period === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
              </>
            )}
          </div>

          {period !== 'custom' && (
            <p className="text-sm text-muted-foreground">
              Selected range: {format(new Date(dateRange.start), 'MMM d, yyyy')} - {format(new Date(dateRange.end), 'MMM d, yyyy')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* PDF Report */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Financial Report (PDF)
          </CardTitle>
          <CardDescription>
            Generate a comprehensive PDF report including income, expenses, category breakdown,
            bills status, and recent transactions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleExportPdf} disabled={loadingPdf}>
            {loadingPdf ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download PDF Report
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* CSV Exports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            CSV Exports
          </CardTitle>
          <CardDescription>
            Export your data as CSV files for use in spreadsheet applications.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={() => handleExportCsv('transactions')}
              disabled={loadingCsv === 'transactions'}
            >
              {loadingCsv === 'transactions' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Transactions
            </Button>

            <Button
              variant="outline"
              onClick={() => handleExportCsv('bills')}
              disabled={loadingCsv === 'bills'}
            >
              {loadingCsv === 'bills' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Bills
            </Button>

            <Button
              variant="outline"
              onClick={() => handleExportCsv('categories')}
              disabled={loadingCsv === 'categories'}
            >
              {loadingCsv === 'categories' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Categories
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Note: Transactions export uses the selected date range. Bills and Categories export all records.
          </p>
        </CardContent>
      </Card>

      {/* Tax Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Tax Export
          </CardTitle>
          <CardDescription>
            Export all tax-deductible transactions for a given year as CSV or PDF.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Tax Year</Label>
              <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(parseInt(v))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => handleTaxExport('csv')}
              disabled={taxExportLoading !== null}
            >
              {taxExportLoading === 'csv' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-2 h-4 w-4" />
              )}
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleTaxExport('pdf')}
              disabled={taxExportLoading !== null}
            >
              {taxExportLoading === 'pdf' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Net Worth Trend */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Net Worth Trend</CardTitle>
            <div className="flex gap-1">
              {[
                { label: '3mo', value: 3 },
                { label: '6mo', value: 6 },
                { label: '12mo', value: 12 },
                { label: 'All', value: 120 },
              ].map(({ label, value }) => (
                <Button
                  key={label}
                  size="sm"
                  variant={netWorthMonths === value ? 'default' : 'outline'}
                  onClick={() => setNetWorthMonths(value)}
                  className="h-7 px-2 text-xs"
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          <CardDescription>Your total assets minus liabilities over time</CardDescription>
        </CardHeader>
        <CardContent>
          {!netWorthHistory?.snapshots?.length ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">
              No net worth history yet. Snapshots are recorded daily at midnight.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={netWorthHistory.snapshots}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="snapshot_date"
                  tickFormatter={(d) => format(parseISO(d), 'MMM d')}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(v) => `$${Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), 'Net Worth']}
                  labelFormatter={(label) => format(parseISO(label as string), 'MMMM d, yyyy')}
                />
                <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="net_worth"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
